import React, { useState } from 'react';
import { 
    List, 
    ListItem, 
    ListItemText, 
    ListItemAvatar, 
    Avatar, 
    Typography, 
    Box,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { formatDistanceToNow } from 'date-fns';
import { auth } from '../../firebaseConfig';
import GroupIcon from '@mui/icons-material/Group';

const ChatList = ({ chats, users, onSelectChat, onStartNewChat, selectedChat }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showNewChat, setShowNewChat] = useState(false);

    const filteredUsers = users.filter(user => 
        user.id !== auth.currentUser?.uid &&
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getChatName = (chat) => {
        if (chat.isGroup) {
            return chat.groupName;
        }
        
        const otherParticipantId = chat.participants.find(id => id !== auth.currentUser?.uid);
        const otherUser = users.find(user => user.id === otherParticipantId);
        return otherUser?.displayName || 'Unknown User';
    };

    const getChatAvatar = (chat) => {
        if (chat.isGroup) {
            return <GroupIcon />;
        }
        
        const otherParticipantId = chat.participants.find(id => id !== auth.currentUser?.uid);
        const otherUser = users.find(user => user.id === otherParticipantId);
        return otherUser?.displayName?.charAt(0) || 'U';
    };

    const getLastMessageTime = (chat) => {
        if (!chat.lastMessageTime) return '';
        return formatDistanceToNow(chat.lastMessageTime.toDate(), { addSuffix: true });
    };

    const filteredChats = chats.filter(chat => {
        const chatName = getChatName(chat).toLowerCase();
        const searchTermLower = searchTerm.toLowerCase();
        return chatName.includes(searchTermLower);
    });

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Chats</Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search or start new chat..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowNewChat(!showNewChat)}>
                                    <PersonAddIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                {showNewChat ? (
                    <List>
                        {filteredUsers.map(user => (
                            <ListItem 
                                key={user.id}
                                button
                                onClick={() => {
                                    onStartNewChat(user.id);
                                    setShowNewChat(false);
                                    setSearchTerm('');
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar>{user.displayName?.charAt(0) || 'U'}</Avatar>
                                </ListItemAvatar>
                                <ListItemText 
                                    primary={user.displayName}
                                    secondary={user.unitNumber || 'No unit number'}
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <List>
                        {filteredChats.map((chat) => (
                            <ListItem
                                key={chat.id}
                                button
                                onClick={() => onSelectChat(chat)}
                                selected={selectedChat?.id === chat.id}
                                sx={{
                                    '&:hover': { bgcolor: 'action.hover' },
                                    '&.Mui-selected': { bgcolor: 'action.selected' }
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar>
                                        {getChatAvatar(chat)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="subtitle1">
                                                {getChatName(chat)}
                                            </Typography>
                                            {chat.isGroup && (
                                                <Chip 
                                                    label="Group" 
                                                    size="small" 
                                                    color="primary" 
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {chat.lastMessage || 'No messages yet'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {getLastMessageTime(chat)}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </Box>
    );
};

export default ChatList; 